import React, {useState} from 'react'

type MixBlendMode =
  | 'normal'
  | 'multiply'
  | 'screen'
  | 'overlay'
  | 'darken'
  | 'lighten'
  | 'color-dodge'
  | 'color-burn'
  | 'hard-light'
  | 'soft-light'
  | 'difference'
  | 'exclusion'
  | 'hue'
  | 'saturation'
  | 'color'
  | 'luminosity'

export interface ImageProps {
  alt: string
  src: string
  srcWidths?: { [key: string]: number }
  height?: number
  width?: number
  classes?: string
  wrapperClasses?: string
  srcFormat?: boolean
  style?: React.CSSProperties
  blendMode?: MixBlendMode
  zoomable?: boolean
  zoomedScale?: number
}

const getSizedImageUrl = (src: string, size: number | null): string => {
  if (size === null) return src
  const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp|jpg.webp)(\?v=\d+)?$/i)
  if (!match) return src
  const prefix = src.split(match[0])
  const suffix = match[0]
  return `${prefix[0]}_${size}x${suffix}`.replace(/http(s)?:/, '')
}

const Image: React.FC<ImageProps> = ({
  alt,
  src,
  srcWidths = {},
  height,
  width,
  classes = '',
  wrapperClasses = '',
  srcFormat = true,
  style = {},
  blendMode = undefined,
  zoomable = false,
  zoomedScale = 2,
}) => {
  if (!src) return

  const [isZoomed, setIsZoomed] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })

  const bps: { [key: string]: number } = { sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 }
  const widths = srcWidths
  // Remove undefined breakpoints
  Object.keys(bps).forEach((key) => !widths[key] && delete bps[key])
  Object.keys(widths).forEach((key) => !bps[key] && delete widths[key])

  // Reduce to values arrays
  const srcWidthsReduced = Object.values(widths)
  const sizeWidthsReduced = Object.values(bps)

  // Convert to srcset and sizes values
  const srcsetFormatted = srcWidthsReduced && srcWidthsReduced.map((w) => `${getSizedImageUrl(src, w)} ${w}w`).join(',')
  const sizesFormatted =
    srcWidthsReduced && srcWidthsReduced.length > 1
      ? `${srcWidthsReduced
          .map((w, i) => `(max-width: ${sizeWidthsReduced[i]}px) ${w}px`)
          .slice(0, srcWidthsReduced.length - 1)
          .join(',')}, ${srcWidthsReduced[srcWidthsReduced.length - 1]}px`
      : `${srcWidthsReduced[0]}px`

  const isMobileDevice = () => {
    return (typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches) ||
        (typeof navigator !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))
  }

  const handleClick = () => {
    if (isMobileDevice() || !zoomable) return

    setIsZoomed(!isZoomed)
    if (isZoomed) {
      setPosition({x: 0, y: 0})
    }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isMobileDevice() || !isZoomed) return

    const rect = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) - 0.5
    const y = ((e.clientY - rect.top) / rect.height) - 0.5

    setPosition({
      x: -x * 50, // Percentage offset
      y: -y * 50  // Percentage offset
    })
  }

  const getCursorStyle = () => {
    if (isMobileDevice()) return 'auto'
    return zoomable ? isZoomed ? 'zoom-out' : 'zoom-in' : 'pointer'
  }

  const isReflection = classes.includes('image-reflection'); // Check if this is a reflection image

  return (
    <div className={`relative ${wrapperClasses}`}>
      <div
          className="overflow-hidden relative"
          onClick={handleClick}
          onMouseMove={handleMouseMove}
          style={{ cursor: getCursorStyle() }}
      >
        <img
            className={`image transition-transform ${classes}`}
            style={{
              ...style,
              mixBlendMode: blendMode,
              transform: isZoomed
                ? `scale(${zoomedScale}) translate(${position.x}%, ${position.y}%) ${isReflection ? 'scaleY(-1)' : ''}`
                : `scale(1) ${isReflection ? 'scaleY(-1)' : ''}`,
              transformOrigin: 'center',
              transition: 'transform 0.2s ease-out'
            }}
            srcSet={srcFormat ? srcsetFormatted : ''}
            sizes={sizesFormatted}
            src={src}
            alt={alt}
            height={height}
            width={width}
            loading="lazy"
            draggable="false"
        />
      </div>
    </div>
  )
}

// Usage example
/**
 * Example usage of the Image component:
 *
 * ```tsx
 * import Image from '@components/atoms/Image';
 *
 * const App: React.FC = () => {
 *   return (
 *     <div>
 *       <h1>Responsive Image</h1>
 *       <Image
 *         alt="Example Image"
 *         src="https://example.com/image.jpg"
 *         srcWidths={{sm: 640,md: 768,lg: 1024,xl: 1280,'2xl': 1536}}
 *         height={300}
 *         width={400}
 *         classes="custom-class"
 *         style={{ key: property }}
 *         blendMode="multiply" // Apply blend mode
 *       />
 *     </div>
 *   );
 * };
 *
 * export default App;
 * ```
 */

export default Image
